// ABOUTME: Top toolbar component with navigation and user menu.
// ABOUTME: Displays app title, breadcrumbs, and user actions.

import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Settings, LogOut, User, Home, MessageSquare, ChevronDown, Sun, Moon, Database } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import { Button } from '@/components/ui/Button';
import { WorkspaceSwitcher } from '@/components/workspace/WorkspaceSwitcher';

interface ToolbarProps {
  showPinboardToggle?: boolean;
}

export function Toolbar({ showPinboardToggle: _showPinboardToggle = true }: ToolbarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { toggleTheme, isDark } = useTheme();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  const isHome = location.pathname === '/';
  const isDataSources = location.pathname.startsWith('/data-sources');
  const isInvestigations = location.pathname.startsWith('/investigations');
  const isOnboarding = location.pathname === '/onboarding';

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    setIsUserMenuOpen(false);
    logout();
  };

  return (
    <header className="h-14 flex items-center justify-between px-4 border-b border-surface-200 bg-surface-0 relative z-30">
      {/* Left: Logo and Navigation */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 hover:opacity-80 transition-all"
        >
          <img
            src="/oleum-logo.png"
            alt="Oleum BI"
            className="w-8 h-8 object-contain"
          />
          <span className="font-semibold text-surface-800 hidden sm:inline">
            Oleum BI
          </span>
        </button>

        {/* Hide navigation on onboarding page */}
        {!isOnboarding && (
          <>
            <div className="h-5 w-px bg-surface-200" />

            {/* Navigation Tabs */}
            <nav className="flex items-center gap-1">
              <button
                onClick={() => navigate('/')}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-sm text-sm font-medium transition-all ${
                  isHome
                    ? 'nav-tab-active'
                    : 'nav-tab'
                }`}
              >
                <Home className="w-4 h-4" />
                <span className="hidden md:inline">Home</span>
              </button>
              <button
                onClick={() => navigate('/data-sources')}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-sm text-sm font-medium transition-all ${
                  isDataSources
                    ? 'nav-tab-active'
                    : 'nav-tab'
                }`}
              >
                <Database className="w-4 h-4" />
                <span className="hidden md:inline">Data Sources</span>
              </button>
              <button
                onClick={() => navigate('/investigations')}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-sm text-sm font-medium transition-all ${
                  isInvestigations
                    ? 'nav-tab-active'
                    : 'nav-tab'
                }`}
              >
                <MessageSquare className="w-4 h-4" />
                <span className="hidden md:inline">Investigations</span>
              </button>
            </nav>
          </>
        )}
      </div>

      {/* Right: Actions */}
      <div className="flex items-center gap-2">
        {/* Theme Toggle */}
        <button
          onClick={toggleTheme}
          className="relative w-9 h-9 rounded-sm flex items-center justify-center hover:bg-surface-100 transition-colors overflow-hidden"
          aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
          title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
        >
          <AnimatePresence mode="wait" initial={false}>
            {isDark ? (
              <motion.div
                key="moon"
                initial={{ y: -20, opacity: 0, rotate: -90 }}
                animate={{ y: 0, opacity: 1, rotate: 0 }}
                exit={{ y: 20, opacity: 0, rotate: 90 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
              >
                <Moon className="w-4 h-4 text-primary-400" />
              </motion.div>
            ) : (
              <motion.div
                key="sun"
                initial={{ y: 20, opacity: 0, rotate: 90 }}
                animate={{ y: 0, opacity: 1, rotate: 0 }}
                exit={{ y: -20, opacity: 0, rotate: -90 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
              >
                <Sun className="w-4 h-4 text-accent-500" />
              </motion.div>
            )}
          </AnimatePresence>
        </button>

        {/* Workspace Switcher */}
        <WorkspaceSwitcher />

        {/* Admin Link (if admin) */}
        {user?.role === 'admin' && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/admin')}
            aria-label="Admin settings"
          >
            <Settings className="w-4 h-4" />
          </Button>
        )}

        {/* User Menu */}
        <div
          ref={userMenuRef}
          data-testid="user-menu"
          className="relative flex items-center gap-2 pl-2 ml-2 border-l border-surface-200"
        >
          <button
            onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
            className="flex items-center gap-2 px-2 py-1 rounded-sm hover:bg-surface-100 transition-colors"
          >
            <div className="relative">
              {user?.picture ? (
                <img
                  src={user.picture}
                  alt={user.name}
                  className="w-7 h-7 rounded-full"
                />
              ) : (
                <div className="w-7 h-7 rounded-full bg-surface-100 flex items-center justify-center">
                  <User className="w-4 h-4 text-surface-500" />
                </div>
              )}
            </div>
            <span className="text-sm text-surface-600 hidden md:inline">
              {user?.name || 'User'}
            </span>
            <ChevronDown className={`w-4 h-4 text-surface-400 transition-transform ${isUserMenuOpen ? 'rotate-180' : ''}`} />
          </button>

          {/* Dropdown Menu */}
          {isUserMenuOpen && (
            <div className="absolute top-full right-0 mt-2 w-56 card shadow-lg py-1 z-50">
              <button
                onClick={handleLogout}
                data-testid="logout-button"
                className="w-full flex items-center gap-2 px-4 py-2 text-sm text-surface-600 hover:text-surface-800 hover:bg-surface-50 transition-colors"
              >
                <LogOut className="w-4 h-4" />
                Log Out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

